import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import {
  SITES_MANAGEMENT_PATH,
  SITE_INFO_PATH,
  GATEWAY_PATH_ROOT,
  ASSET_PATH_ROOT,
  ALARM_RULES_PATH_ROOT,
  INTEGRATIONS_PATH,
  CONTROLLERS_PATH_ROOT,
  ALARMS_PATH,
  ALARMS_NOTIFICATIONS_PATH,
  SITES_MAP_PATH,
  CREATE_DASHBOARD,
  PREVIEW_DASHBOARD,
  USER_ACCOUNTS_PATH_ROOT,
  ACCOUNT_BILLING_INFORMATION_PATH,
} from 'src/routes/config';
import {
  SITE_INFO_SUBITEM,
  GATEWAYS_SUBITEM,
  SITE_LIST_SUBITEM,
  SITE_MAP_SUBITEM,
  ASSETS_SUBITEM,
  ALARM_RULES_SUBITEM,
  INTEGRATIONS_SUBITEM,
  CONTROLLERS_SUBITEM,
  ALARMS_LIST_SUBITEM,
  MANAGE_ALARMS_NOTIFICATIONS_SUBITEM,
  CREATE_DASHBOARD_SUB_ITEM,
} from '../config';
import { NavSubItemProps } from '../types';

export const useSubNavigation = (subItems: NavSubItemProps[]) => {
  const location = useLocation();
  const { dashboardId } = useParams();

  const isSiteManagementPage = location.pathname.includes(SITES_MANAGEMENT_PATH);
  const isSiteMapPage = location.pathname.includes(SITES_MAP_PATH);
  const isSiteInfoPage = location.pathname.includes(SITE_INFO_PATH);
  const isGatewaysPage = location.pathname.includes(GATEWAY_PATH_ROOT);
  const isAssetsPage = location.pathname.includes(ASSET_PATH_ROOT);
  const isAlarmRulesManagementPage = location.pathname.includes(ALARM_RULES_PATH_ROOT);
  const isIntegrationsPage = location.pathname.includes(INTEGRATIONS_PATH);
  const isControllersPage = location.pathname.includes(CONTROLLERS_PATH_ROOT);
  const isAlarmsPage =
    location.pathname.includes(ALARMS_PATH) &&
    !location.pathname.includes(ALARMS_NOTIFICATIONS_PATH);
  const isAlarmsNotificationPage = location.pathname.includes(ALARMS_NOTIFICATIONS_PATH);
  const isUsersPage = location.pathname.includes(USER_ACCOUNTS_PATH_ROOT);
  const isBillingInformationPage = location.pathname.includes(ACCOUNT_BILLING_INFORMATION_PATH);
  const isCreateDashboardPage = location.pathname.includes(CREATE_DASHBOARD);
  const isPreviewDashboardPage = location.pathname.includes(PREVIEW_DASHBOARD);

  const [subItemsActive, setSubItemsActive] = useState(
    subItems.map(({ title, path }) => ({
      title,
      path,
      isActive: false,
    }))
  );

  const onSubitemClickHandler = (path: string) => {
    setSubItemsActive((prev) =>
      prev.map((item) => ({
        ...item,
        isActive: item.path === path,
      }))
    );
  };

  useEffect(() => {
    setSubItemsActive(() =>
      subItems.map(({ title, path }) => {
        if (
          title !== CREATE_DASHBOARD_SUB_ITEM &&
          path.includes(PREVIEW_DASHBOARD) &&
          dashboardId
        ) {
          return { title, path, isActive: path.endsWith(dashboardId) };
        }
        if (isPreviewDashboardPage && location.pathname === path)
          return { title, path, isActive: true };

        if (isCreateDashboardPage && title === CREATE_DASHBOARD_SUB_ITEM)
          return { title, path, isActive: true };

        if (isSiteManagementPage && title === SITE_LIST_SUBITEM)
          return { title, path, isActive: true };
        if (isSiteMapPage && title === SITE_MAP_SUBITEM) return { title, path, isActive: true };
        if (isSiteInfoPage && title === SITE_INFO_SUBITEM) return { title, path, isActive: true };
        if (isGatewaysPage && title === GATEWAYS_SUBITEM) return { title, path, isActive: true };
        if (isAssetsPage && title === ASSETS_SUBITEM) return { title, path, isActive: true };
        if (isAlarmRulesManagementPage && title === ALARM_RULES_SUBITEM)
          return { title, path, isActive: true };
        if (isIntegrationsPage && title === INTEGRATIONS_SUBITEM)
          return { title, path, isActive: true };
        if (isControllersPage && title === CONTROLLERS_SUBITEM)
          return { title, path, isActive: true };
        if (isAlarmsPage && title === ALARMS_LIST_SUBITEM) return { title, path, isActive: true };
        if (isAlarmsNotificationPage && title === MANAGE_ALARMS_NOTIFICATIONS_SUBITEM)
          return { title, path, isActive: true };
        if (isUsersPage && path === USER_ACCOUNTS_PATH_ROOT) return { title, path, isActive: true };
        if (isBillingInformationPage && path === ACCOUNT_BILLING_INFORMATION_PATH)
          return { title, path, isActive: true };

        return { title, path, isActive: false };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, dashboardId]);

  return { subItemsActive, onSubitemClickHandler };
};
